import React from 'react';
import PropTypes from 'prop-types';
import { connect } from '@gasbuddy/react';
import { GoogleMap, LoadScriptNext } from '@react-google-maps/api';
import { Loader } from '@gasbuddy/react-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/free-solid-svg-icons';
import { faCircle } from '@fortawesome/free-regular-svg-icons';

import StationPropTypes from '../../../utils/prop-types/station';
import DateRangePropType from '../../../utils/prop-types/date-range';
import styles from './styles.css';
import { MapPosition } from '../../../utils/prop-types/common-types';
import StationMapPin from './station-map-pin';
import Notification from '../../notification';

const { api, getGoogleMapsAPIKey, convertStarRatingToHexColor, convertCompetitiveIndexToHexColor } = require('../../../../lib/bputils').default;

const COMP_INDEX = 'competitive-index';
const STAR_RATING = 'star-rating';

const defaultMapOptions = {
  fullscreenControl: false, // Hide fullscreen-view button
  streetViewControl: false, // Hide Street-view button
  mapTypeControl: false, // Hides map/satellite toggle
};

const RatingsMapLegend = () => (
  <div className={styles.ratingLegend}>
    <div className={styles.legendBar}>
      <div className={`${styles.poor} ${styles.legendItem}`} />
      <div className={styles.legendItemTitle}>Poor</div>
    </div>
    <div className={styles.legendBar}>
      <div className={`${styles.neutral} ${styles.legendItem}`} />
      <div className={styles.legendItemTitle}>Neutral</div>
    </div>
    <div className={styles.legendBar}>
      <div className={`${styles.great} ${styles.legendItem}`} />
      <div className={styles.legendItemTitle}>Great</div>
    </div>
  </div>
);

const MapDataToggle = ({ onToggleCallback }) => {
  const [dataToggle, setDataToggle] = React.useState(STAR_RATING);

  React.useEffect(() => {
    onToggleCallback(STAR_RATING);
  }, []);

  const handleOnToggle = (value) => {
    if (value !== dataToggle) {
      setDataToggle(value);
      onToggleCallback(value);
    }
  };

  return (
    <div className={styles.mapDataToggle}>
      <button
        type="button"
        className={`${styles.dataToggleButton} ${styles.leftToggle} ${dataToggle === STAR_RATING ? styles.activeToggle : ''}`}
        onClick={() => handleOnToggle(STAR_RATING)}
      >
        <span className={styles.toggleLabel}>Show By</span>
        <span className={styles.toggleValue}>Star Ratings</span>
      </button>
      <button
        type="button"
        className={`${styles.dataToggleButton} ${styles.rightToggle} ${dataToggle === COMP_INDEX ? styles.activeToggle : ''}`}
        onClick={() => handleOnToggle(COMP_INDEX)}
      >
        <span className={styles.toggleLabel}>Show By</span>
        <span className={styles.toggleValue}>Competitive Index</span>
      </button>
    </div>
  );
};

MapDataToggle.propTypes = {
  onToggleCallback: PropTypes.func.isRequired,
};

function StationCompetitiorsMap({ station, dateRange, mapCenter }) {
  const [nearbyStations, setNearbyStations] = React.useState([]);
  const [fetchingNearbyStations, setFetchingNearbyStations] = React.useState(true);
  const [dataToggle, setDataToggle] = React.useState(STAR_RATING);
  const [brandEngagement, setBrandEngagement] = React.useState(null);
  const [stationStats, setStationStats] = React.useState(null);
  const [fetchingStationData, setFetchingStationData] = React.useState(true);

  const mapRef = React.useRef();
  const mapAPIKey = getGoogleMapsAPIKey();

  const updateMapBounds = (m) => {
    const bounds = new window.google.maps.LatLngBounds();
    nearbyStations.forEach((nearbyStation) => {
      const { latitude, longitude } = nearbyStation;
      const latLng = new window.google.maps.LatLng(latitude, longitude);
      bounds.extend(latLng);
    });

    // include the station itself in the bounds.
    const { latitude, longitude } = station;
    const latLng = new window.google.maps.LatLng(latitude, longitude);
    bounds.extend(latLng);

    m.fitBounds(bounds);
  };

  const getNearbyStations = async () => {
    setFetchingNearbyStations(true);
    const { id } = station;
    const { startDate, endDate } = dateRange;
    try {
      const response = await api.get({
        path: `/stations/${id}/nearby`,
        requestId: 'getNearbyStations',
        data: {
          start_date: startDate.format('YYYY-MM-DD'),
          end_date: endDate.format('YYYY-MM-DD'),
        },
      });
      setNearbyStations(response);
      setFetchingNearbyStations(false);
    } catch (err) {
      Notification.error('Unable to fetch nearby station data');
    }
  };

  const getStationRatings = async () => {
    setFetchingStationData(true);
    const { startDate, endDate } = dateRange;
    const { id } = station;
    try {
      const [stationStatsRes, brandEngagementRes] = await Promise.all([
        api.get({
          path: `/stats/ratings/stations/${id}`,
          requestId: 'stationStatsRatings',
          data: {
            start_date: startDate.format('YYYY-MM-DD'),
            end_date: endDate.format('YYYY-MM-DD'),
          },
        }),
        api.get({
          path: `/stats/engagement/stations/${id}`,
          requestId: 'stationStatsEngagement',
          data: {
            start_date: startDate.format('YYYY-MM-DD'),
            end_date: endDate.format('YYYY-MM-DD'),
          },
        }),
      ]);
      setStationStats(stationStatsRes);
      setBrandEngagement(brandEngagementRes);
    } catch (err) {
      Notification.error('Unable to fetch station data');
    } finally {
      setFetchingStationData(false);
    }
  };

  const getNearbyStationBorderColor = (nearbyStation) => {
    if (dataToggle === STAR_RATING) {
      return convertStarRatingToHexColor(nearbyStation.star_rating);
    }
    return convertCompetitiveIndexToHexColor(nearbyStation.competitive_index);
  };

  const showValue = value => (value !== 0 ? value : 'N/A');

  React.useEffect(() => {
    if (station.id && dateRange) {
      getNearbyStations();
      getStationRatings();
    }
  }, [station.id, dateRange]);

  const onMapLoadHandler = (m) => {
    updateMapBounds(m);
  };

  const renderPinHeader = (starRating, compIndex) => (
    <div>
      <FontAwesomeIcon icon={dataToggle === STAR_RATING ? faStar : faCircle} />
      &nbsp;&nbsp;
      {showValue(dataToggle === STAR_RATING ? starRating : compIndex)}
    </div>
  );

  return (
    <div className={styles.mapContainer}>
      <LoadScriptNext
        googleMapsApiKey={mapAPIKey}
        loadingElement={(<Loader className={`${styles.loader} ${styles.mapContainer}`} />)}
        version="3.48"
      >
        {(fetchingNearbyStations && fetchingStationData) ? <Loader className={`${styles.loader} ${styles.mapContainer}`} /> : (
          <GoogleMap
            ref={mapRef}
            mapContainerClassName={styles.mapContainer}
            defaultZoom={13}
            defaultCenter={mapCenter}
            options={defaultMapOptions}
            onLoad={onMapLoadHandler}
          >
            {
              nearbyStations.map(nearbyStation => (
                <StationMapPin
                  key={nearbyStation.id}
                  station={nearbyStation}
                  title={renderPinHeader(nearbyStation.star_rating, nearbyStation.competitive_index)}
                  headerColor={getNearbyStationBorderColor(nearbyStation)}
                />
              ))
            }
            <StationMapPin
              key={station.id}
              station={station}
              title={renderPinHeader(stationStats?.star_rating, brandEngagement?.competitive_index)}
            />
          </GoogleMap>
        )}
        <RatingsMapLegend />
        <MapDataToggle onToggleCallback={setDataToggle} />
      </LoadScriptNext>
    </div>
  );
}

StationCompetitiorsMap.propTypes = {
  station: StationPropTypes,
  dateRange: DateRangePropType,
  mapCenter: MapPosition.isRequired,
};

StationCompetitiorsMap.defaultProps = {
  station: null,
  dateRange: null,
};

const mapStateToProps = state => ({
  station: state.station,
  isLoading: state.isLoading,
  dateRange: state.dateRange,
  mapCenter: {
    lat: state.station.latitude,
    lng: state.station.longitude,
  },
});

export default connect(mapStateToProps, null)(StationCompetitiorsMap);
